/**
 * Longest we let an idle callback wait before running anyway. Long enough for a busy
 * page to reach a genuine idle period, short enough that deferred work still happens
 * on a page that never fully settles.
 */
const IDLE_TIMEOUT_MS = 2000;

interface IIdleWindow {
  requestIdleCallback?: (
    callback: () => void,
    options?: { timeout: number },
  ) => number;
  cancelIdleCallback?: (handle: number) => void;
}

/**
 * Runs `callback` once the page has finished loading and the browser is idle, so work
 * that isn't needed for first paint can't delay it.
 *
 * Waits for the `load` event first, then asks for idle time. The load wait is the part
 * that matters: idle periods also occur *during* loading, so `requestIdleCallback` alone
 * can fire before first contentful paint on a fast device — the opposite of the intent.
 *
 * `requestIdleCallback` is unavailable in Safari and iOS WKWebView (and in jsdom), where
 * this falls back to a macrotask. That fallback is the primary path on iOS rather than an
 * edge case, so it has to be correct, not merely present.
 *
 * @returns cancel function; safe to call after the callback has already run.
 */
export function whenPageIsIdle(callback: () => void): () => void {
  const idleWindow = window as typeof window & IIdleWindow;
  const requestIdle = idleWindow.requestIdleCallback;

  let cancelled = false;
  let timeoutId: number | undefined;
  let idleHandle: number | undefined;

  const run = () => {
    if (!cancelled) {
      callback();
    }
  };

  const whenIdle = () => {
    if (cancelled) {
      return;
    }

    if (requestIdle) {
      // Called off the window so implementations that check their receiver still work.
      idleHandle = requestIdle.call(idleWindow, run, {
        timeout: IDLE_TIMEOUT_MS,
      });
      return;
    }

    // No idle scheduling: yield one macrotask so the callback lands on its own task
    // rather than extending whatever is running now.
    timeoutId = window.setTimeout(run, 0);
  };

  const onLoad = () => {
    whenIdle();
  };

  if (document.readyState === "complete") {
    whenIdle();
  } else {
    window.addEventListener("load", onLoad, { once: true });
  }

  return () => {
    cancelled = true;
    window.removeEventListener("load", onLoad);
    clearTimeout(timeoutId);
    if (idleHandle !== undefined) {
      idleWindow.cancelIdleCallback?.(idleHandle);
    }
  };
}
